import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { schoolFromToken } from "./schoolInfo";
import { SessionContext } from "../SessionProvider";
import { usePermissions } from "../contexts/PermissionContext";
import reportCardApi, { errorMessage } from "./reportCardApi";
import ClassSectionPicker from "./ClassSectionPicker";
import PageShell from "./PageShell";
import ReportCardView from "./ReportCardView";

/** Print the given DOM node(s) in a hidden iframe so the app layout is not printed. */
const printNode = (node, title) => {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  // copy page styles (bootstrap + reportCard.css already injected by the bundler)
  const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).map((s) => s.outerHTML).join("\n");
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><base href="${window.location.origin}/"><title>${title || "Report Card"}</title>${styles}
    <style>@page { size: A4 portrait; margin: 6mm; } body { background:#fff; margin:0; }</style>
    </head><body>${node.innerHTML}</body></html>`);
  doc.close();
  const go = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  };
  // wait for images (logo) to load
  const imgs = Array.from(doc.images);
  if (!imgs.length) setTimeout(go, 300);
  else Promise.all(imgs.map((i) => (i.complete ? 1 : new Promise((r) => { i.onload = r; i.onerror = r; })))).then(() => setTimeout(go, 200));
};

/**
 * Route: /reportcard/publish
 * Class/section list -> preview live card, publish (snapshot), withdraw, print published cards.
 */
const ReportCardPublish = () => {
  const { selectedSessionId } = useContext(SessionContext);
  const { rolePermissions, userInfo } = usePermissions();
  const canPublish =
    userInfo?.userrole === "SYS_ADMIN" || userInfo?.userrole === "ADMIN" || !!rolePermissions?.Exam_List?.can_edit;

  const [filter, setFilter] = useState({ class_id: "", section_id: "" });
  const [examGroups, setExamGroups] = useState([]);
  const [examGroupId, setExamGroupId] = useState(""); // "" = annual report
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [onlyWithResult, setOnlyWithResult] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {title, reports:[...]}
  const printRef = useRef(null);
  const school = useMemo(schoolFromToken, []);

  useEffect(() => {
    reportCardApi.examGroups.list().then((l) => setExamGroups(l.filter((g) => g.status === "active"))).catch(() => {});
  }, []);

  const ready = filter.class_id && filter.section_id && selectedSessionId;
  const params = { sessionid: selectedSessionId, exam_group_id: examGroupId || undefined };

  const load = async () => {
    if (!ready) return setRows([]);
    try {
      const res = await reportCardApi.publishStatus({ ...filter, ...params });
      setRows(res.records || []);
      setSelected([]);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, [filter.class_id, filter.section_id, examGroupId, selectedSessionId]);

  // only students whose result (marks) has been entered can be published / printed
  const withResult = rows.filter((r) => r.has_result);
  const shownRows = onlyWithResult ? withResult : rows;
  const noResultCount = rows.length - withResult.length;
  const incomplete = withResult.filter((r) => r.papers_entered < r.papers_total);

  const allSelected = withResult.length > 0 && selected.length === withResult.length;
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const targetIds = selected.length ? selected : withResult.map((r) => r.student_addmission_id);

  const openPreview = async (row) => {
    setBusy(true);
    try {
      const res = await reportCardApi.build(row.student_addmission_id, params);
      setPreview({ title: `Preview – ${row.student_name} (not published)`, reports: [res.report] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const openPublished = async (ids) => {
    const list = rows.filter((r) => ids.includes(r.student_addmission_id) && r.publish?.status === "PUBLISHED");
    if (!list.length) return toast.error("None of the selected students has a published report card");
    setBusy(true);
    try {
      const reports = [];
      for (const r of list) {
        const res = await reportCardApi.published(r.student_addmission_id, params);
        reports.push(res.snapshot);
      }
      const skipped = ids.length - list.length;
      setPreview({ title: `Published report cards (${reports.length})${skipped ? ` – ${skipped} not published, skipped` : ""}`, reports, printable: true });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    const n = targetIds.length;
    if (!n) return toast.error("No student with a result (marks) to publish");
    const inc = incomplete.filter((r) => targetIds.includes(r.student_addmission_id)).length;
    const warn = inc ? `\n\n${inc} student(s) have marks missing for some papers.` : "";
    if (!window.confirm(`Publish ${n} report card(s)? Marks, grades and remarks will be frozen as they are now. Already published cards get a new version.${warn}`)) return;
    setBusy(true);
    try {
      const res = await reportCardApi.publish({
        sessionid: selectedSessionId,
        exam_group_id: examGroupId || null,
        issue_date: issueDate,
        student_addmission_ids: targetIds,
      });
      if (res.failed?.length) toast.error(`${res.failed.length} failed: ${res.failed[0].message}`);
      if (res.skipped?.length) toast.info(`${res.skipped.length} skipped – no marks entered`);
      toast.success(`${res.published?.length || 0} report card(s) published`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    const ids = targetIds.filter((id) => rows.find((r) => r.student_addmission_id === id)?.publish?.status === "PUBLISHED");
    if (!ids.length) return toast.error("Nothing published to withdraw");
    if (!window.confirm(`Withdraw ${ids.length} published report card(s)? Parents will no longer see them until you publish again.`)) return;
    setBusy(true);
    try {
      const res = await reportCardApi.withdraw({ sessionid: selectedSessionId, exam_group_id: examGroupId || null, student_addmission_ids: ids });
      toast.success(`${res.withdrawn?.length || 0} withdrawn`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = (p) => {
    if (!p) return <Badge bg="secondary">Not published</Badge>;
    if (p.status === "PUBLISHED") return <Badge bg="success">Published v{p.version}</Badge>;
    if (p.status === "WITHDRAWN") return <Badge bg="warning" text="dark">Withdrawn v{p.version}</Badge>;
    return <Badge bg="light" text="dark">{p.status}</Badge>;
  };

  const publishedCount = rows.filter((r) => r.publish?.status === "PUBLISHED").length;

  const resultBadge = (r) => {
    if (!r.has_result) return <Badge bg="secondary">No result</Badge>;
    if (r.papers_entered < r.papers_total)
      return <Badge bg="warning" text="dark" title="Some papers have no marks">Incomplete {r.papers_entered}/{r.papers_total}</Badge>;
    return <Badge bg="success">Complete {r.papers_entered}/{r.papers_total}</Badge>;
  };

  return (
    <PageShell title="Preview & Publish">
      <Row className="g-2 mb-3 align-items-end">
        <ClassSectionPicker value={filter} onChange={setFilter} md={3} />
        <Col md={3}>
          <Form.Label className="form-view-label">Report</Form.Label>
          <Form.Select value={examGroupId} onChange={(e) => setExamGroupId(e.target.value)}>
            <option value="">Annual (all terms)</option>
            {examGroups.map((g) => <option key={g.id} value={g.id}>{g.name} only</option>)}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="form-view-label">Issue Date</Form.Label>
          <Form.Control type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
        </Col>
      </Row>

      {!selectedSessionId && <Alert variant="warning">Select a session in the header first.</Alert>}

      {ready && (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
            <div className="d-flex align-items-center gap-3">
              <small className="text-muted">
                {withResult.length} with result · {publishedCount} published
                {incomplete.length > 0 && <span className="text-warning"> · {incomplete.length} incomplete</span>}
                {selected.length > 0 && ` · ${selected.length} selected`}
              </small>
              {noResultCount > 0 && (
                <Form.Check
                  id="rc-only-result"
                  className="small"
                  label={`Hide ${noResultCount} without result`}
                  checked={onlyWithResult}
                  onChange={(e) => setOnlyWithResult(e.target.checked)}
                />
              )}
            </div>
            <div className="d-flex gap-2">
              {busy && <Spinner size="sm" />}
              <Button size="sm" variant="outline-secondary" disabled={busy || !publishedCount} onClick={() => openPublished(targetIds)}>
                <i className="fa fa-print me-1"></i>Print {selected.length ? "Selected" : "All"} Published
              </Button>
              {canPublish && (
                <>
                  <Button size="sm" variant="outline-danger" disabled={busy || !publishedCount} onClick={withdraw}>
                    Withdraw {selected.length ? "Selected" : "All"}
                  </Button>
                  <Button size="sm" variant="success" disabled={busy || !withResult.length} onClick={publish}>
                    Publish {selected.length ? `Selected (${selected.length})` : "All"}
                  </Button>
                </>
              )}
            </div>
          </div>

          <Table bordered size="sm" className="align-middle">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <Form.Check checked={allSelected} onChange={() => setSelected(allSelected ? [] : withResult.map((r) => r.student_addmission_id))} />
                </th>
                <th style={{ width: 60 }}>Roll</th>
                <th>Student</th>
                <th>S.R. No.</th>
                <th>Result</th>
                <th>Status</th>
                <th>Published On</th>
                <th style={{ width: 190 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {shownRows.map((r) => (
                <tr key={r.student_addmission_id} className={r.has_result ? "" : "text-muted"}>
                  <td><Form.Check disabled={!r.has_result} checked={selected.includes(r.student_addmission_id)} onChange={() => toggle(r.student_addmission_id)} /></td>
                  <td>{r.roll_no || ""}</td>
                  <td>{r.student_name}</td>
                  <td>{r.formno || ""}</td>
                  <td>{resultBadge(r)}</td>
                  <td>{statusBadge(r.publish)}</td>
                  <td>{r.publish?.published_at ? new Date(r.publish.published_at).toLocaleString("en-IN") : ""}</td>
                  <td>
                    <Button size="sm" variant="outline-primary" className="me-1" disabled={busy || !r.has_result} onClick={() => openPreview(r)}>Preview</Button>
                    {r.publish?.status === "PUBLISHED" && (
                      <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => openPublished([r.student_addmission_id])}>
                        <i className="fa fa-print"></i> Published
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {shownRows.length === 0 && (
                <tr><td colSpan={8} className="text-center text-muted">
                  {rows.length ? "No student has marks entered yet for this report" : "No active students in this section"}
                </td></tr>
              )}
            </tbody>
          </Table>
        </>
      )}

      <Modal show={!!preview} onHide={() => setPreview(null)} size="xl" scrollable>
        <Modal.Header closeButton><Modal.Title style={{ fontSize: 16 }}>{preview?.title}</Modal.Title></Modal.Header>
        <Modal.Body style={{ background: "#eee" }}>
          <div ref={printRef}>
            {(preview?.reports || []).map((rep, i) => <ReportCardView key={i} report={rep} school={school} />)}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {!preview?.printable && <small className="text-muted me-auto">This is a live preview. Publish to freeze it for printing and parents.</small>}
          <Button variant="secondary" onClick={() => setPreview(null)}>Close</Button>
          <Button onClick={() => printNode(printRef.current, "Report Card")}><i className="fa fa-print me-1"></i>Print</Button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
};

export default ReportCardPublish;
